import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import EpayService from 'services/EpayService/EpayService';
import routeDefinitions from 'enum/routeDefinitions';
import { getHostName } from 'utils/helpers';
import { resolveURLPath } from 'utils/urlHelpers';
import parseCatch from 'utils/parseCatch';
import ApiService from 'services/ApiService/ApiService';

import coreService from './core';
import * as publicApi from './public';
import * as privateApi from './private';
import chatStore from './chatStore';


class FanClubService {
  core = coreService;
  chatStore = chatStore;
  public = publicApi;
  private = privateApi;

  resetToken = coreService.resetToken;

  fetchMembershipDetails = async({ username, accessHash }) => {
    const baseUrl = ConfigurationService.get(ConfigurationKeys.EPAY_SERVER);
    const tenant = ConfigurationService.get(ConfigurationKeys.EPAY_TENANT);
    const endPoint = `rest/v1/checkout/${tenant}/en/FANCLUB`;

    const url = new URL(baseUrl);
    url.pathname = resolveURLPath(url.pathname, endPoint);
    url.searchParams.append('external', true);
    if (accessHash) {
      url.searchParams.append('userName', username);
      url.searchParams.append('accessHash', accessHash);
    }

    const { data } = await ApiService.get(url.toString(), { withCredentials: false });

    return data;
  };

  #fetchSubscription = async({ performer, username }) => {
    const url = coreService.computeURL(`modelsByPartnerIdentifier/${performer}/subscription/buyInfo`);
    url.searchParams.set('purchaserPartnerIdentifier', username);

    const { token } = await coreService.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };

    const { data } = await ApiService.get(url.toString(), { headers });

    return data;
  };

  #computeCheckoutUrl = ({ username, productData }) => {
    const baseUrl = ConfigurationService.get(ConfigurationKeys.EPAY_SERVER);
    const tenant = ConfigurationService.get(ConfigurationKeys.EPAY_TENANT);

    const productId = productData.products[0].id;
    const { processorId } = productData.products[0].categories[0].methods[0];
    const endPoint = `rest/v1/checkout/${tenant}/en/${username}/${productId}/${processorId}`;

    const url = new URL(baseUrl);
    url.pathname = resolveURLPath(url.pathname, endPoint);

    return url;
  };

  #subscribeToFanclub = async({ performer, username, accessHash, jwt }) => {
    const productData = await this.fetchMembershipDetails({ username, accessHash });
    const subscriptionDetails = await this.#fetchSubscription({ performer, username });

    const checkoutUrl = this.#computeCheckoutUrl({ username, productData });

    const { data } = await ApiService.post(checkoutUrl.toString(), {
      withCredentials: false,
      data: {
        external: true,
        accessHash,
        jwt,
        target: performer,
        amount: subscriptionDetails.amount,
        hash: subscriptionDetails.hash,
        siteSubcat: `cam4.com/${performer}`,
        subCategory: subscriptionDetails.subCategory.toString(),
        originalDomain: getHostName(),
        returnPath: routeDefinitions.checkout,
      },
    });

    return data;
  };

  joinMigratedFanclub = async({ username, accessHash, jwt, performer }) => {
    const trySubscribe = await this.#subscribeToFanclub({ performer, username, accessHash, jwt });

    if (trySubscribe.status !== 'SUCCESS') {
      return;
    }

    EpayService.openPaymentProcessorWindow();
    EpayService.prepareForm(trySubscribe.nextStage);

    try {
      EpayService.submitForm();
    } catch (error) {
      parseCatch(error);
      EpayService.submitForm();
    }

    coreService.resetToken();
  };

  // This will be moved in chat namespace
  getConversations = async({ isPerformer }) => {
    const performerEndPoint = 'privateChat/modelConversations';
    const fanEndPoint = 'privateChat/fanConversations';
    const endPoint = isPerformer ? performerEndPoint : fanEndPoint;

    const url = coreService.computeURL(endPoint);

    const { token } = await coreService.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };

    const { data } = await ApiService.get(url.toString(), { headers });

    return data;
  };

  getGalleryPurchaseDetails = async({ modelDisplayName, contentId }) => {
    const url = coreService.computeURL(`models/${modelDisplayName}/storeMediaContentBuyDetails/galleries/${contentId}`);

    const { token } = await coreService.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };

    const { data } = await ApiService.get(url.toString(), { headers });

    return data;
  };

  getVideoPurchaseDetails = async({ modelDisplayName, contentId }) => {
    const url = coreService.computeURL(`models/${modelDisplayName}/storeMediaContentBuyDetails/videos/${contentId}`);

    const { token } = await coreService.getToken();
    const headers = { 'Authorization': `Bearer ${token}` };

    const { data } = await ApiService.get(url.toString(), { headers });

    return data;
  };

  buyGalleryItem = async({ modelDisplayName, profileName, contentId }) => {
    const galleryDetails = await this.getGalleryPurchaseDetails({ modelDisplayName, contentId });
    const baseUrl = ConfigurationService.get(ConfigurationKeys.BASE_URL);

    const endPoint = `rest/v1.0/partner/fanclubs/${profileName}/purchase`;

    const url = new URL(baseUrl);
    url.pathname = resolveURLPath(url.pathname, endPoint);

    const payload = {
      orderId: galleryDetails.orderId,
      amount: galleryDetails.amount,
      hash: galleryDetails.hash,
    };

    const data = await ApiService.post(url.toString(), { data: payload });

    return data;
  };

  buyVideoItem = async({ modelDisplayName, profileName, contentId }) => {
    const videoDetails = await this.getVideoPurchaseDetails({ modelDisplayName, contentId });
    const baseUrl = ConfigurationService.get(ConfigurationKeys.BASE_URL);

    const endPoint = `rest/v1.0/partner/fanclubs/${profileName}/purchase`;

    const url = new URL(baseUrl);
    url.pathname = resolveURLPath(url.pathname, endPoint);

    const payload = {
      orderId: videoDetails.orderId,
      amount: videoDetails.amount,
      hash: videoDetails.hash,
    };

    const data = await ApiService.post(url.toString(), { data: payload });

    return data;
  };
}

export default new FanClubService();
